import { motion } from 'framer-motion';
import React from 'react';

import type { TabsProps } from '@types';
import { cn } from '@utils';

import { TabButton } from './TabButton';

const containerVariants = {
  contained: 'bg-gray-100 rounded-lg p-1',
  underlined: 'border-b border-gray-200',
  pills: 'rounded-lg bg-gray-200/90',
};

export const Tabs: React.FC<TabsProps> = ({
  tabs,
  activeTab,
  onTabChange,
  variant = 'pills',
  size = 'sm',
  fullWidth = false,
  className,
  tabClassName,
  animated = true,
  ariaLabel = 'Navigation tabs',
}) => {
  const containerClasses = cn(
    'flex relative',
    containerVariants[variant],
    fullWidth ? 'w-full justify-between' : 'w-fit',
    variant === 'underlined' ? 'gap-6' : 'gap-1',
    className,
  );

  return (
    <div className={containerClasses} role="tablist" aria-label={ariaLabel}>
      {tabs.map((tab) => (
        <div key={tab.key} className={cn('relative', { 'flex-1': fullWidth })}>
          {animated && (variant === 'contained' || variant === 'pills') && activeTab === tab.key && (
            <motion.div
              layoutId="tab-slider"
              className={cn(
                'absolute inset-0 rounded-md',
                variant === 'contained' ? 'bg-theme-primary-main' : 'shadow-sm border border-gray-200/50',
              )}
              transition={{
                type: 'spring',
                stiffness: 500,
                damping: 30,
              }}
            />
          )}

          <TabButton
            tab={tab}
            isActive={activeTab === tab.key}
            onClick={onTabChange}
            size={size}
            className={cn('relative z-10', tabClassName)}
            animated={animated}
          />
        </div>
      ))}
    </div>
  );
};
